import {
    Color,
    Mesh,
    MeshStandardMaterial,
    MeshPhysicalMaterial,
    NoColorSpace,
    PlaneGeometry,
    RepeatWrapping,
    Scene,
    ShadowMaterial,
    SRGBColorSpace,
    MeshBasicMaterial,
    Box3,
    Group,
    Vector3,
    DoubleSide,
} from "three";
import { MeshSurfaceSampler } from "three/examples/jsm/math/MeshSurfaceSampler.js";
import { RoomElementType } from "./enums";
import { GraphicService } from "./GraphicService";
import { IRoomSize, ISceneConfiguration } from "./interfaces";
import { LoaderService } from "./LoaderService";
import { Room } from "./Room";
import { AccessPointDragHandler } from "./AccessPointDragHandler";

const FLOOR_TEXTURE_SIZE = 1.5;
const FLOOR_SHADOW_OPACITY = 0.25;

export class BuilderService {
    _floor: Mesh | undefined;
    _floorShadow: Mesh | undefined;
    _floorGlow: Mesh | undefined;
    _floorSampler: MeshSurfaceSampler | undefined;
    _Ceiling: Mesh | undefined;
    _Logo: Mesh | undefined;
    _CMRLogo: Group | undefined;
    _scene!: Scene;
    _walls: Mesh[] = [];
    _roomSize!: IRoomSize;
    private _loader!: LoaderService;
    private _wallColor!: Color;
    private _floorColor!: Color;
    private M_Floor!: MeshPhysicalMaterial;
    private M_FloorShadow!: ShadowMaterial;
    private M_FloorGlow!: MeshStandardMaterial;
    private M_Logo!: MeshStandardMaterial;
    private M_Ceilling!: MeshBasicMaterial;
    private M_Wall1!: MeshStandardMaterial;
    private M_Wall2!: MeshStandardMaterial;
    private M_WallNorth!: MeshStandardMaterial;
    room!: Room;

    private _accessPointDragHandler: AccessPointDragHandler | undefined;

    /**
     * Build the floor of the room
     */
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    setup(
        sceneConf: ISceneConfiguration,
        loader: LoaderService,
        scene: Scene = GraphicService.scene
    ) {
        this._roomSize = sceneConf.roomSize;
        this._scene = scene;
        this._loader = loader;

        this.room = new Room(JSON.parse(JSON.stringify(sceneConf)));

        this.room.onAccessPointsChanged = () => {
            this.updateCMRLogoVisibility();
        };

        this._wallColor = new Color(this._loader._modelData.room.wall.color);
        this._floorColor = new Color(this._loader._modelData.room.floor.color);

        if (!this.M_Wall1) {
            this.M_Wall1 = new MeshStandardMaterial({
                color: this._wallColor,
                roughness: 0.9,
                metalness: 0,
            });
        }
        if (!this.M_Wall2) {
            this.M_Wall2 = new MeshStandardMaterial({
                color: this._wallColor,
                roughness: 0.9,
                metalness: 0,
            });
        }
        if (!this.M_WallNorth) {
            this.M_WallNorth = new MeshStandardMaterial({
                color: this._wallColor,
                roughness: 0.9,
                metalness: 0,
            });
        }
        this.M_Wall2 = this.M_Wall1;

        if (!this.M_Floor) {
            this.M_Floor = new MeshPhysicalMaterial({
                color: this._floorColor,
                roughness: 1,
                metalness: 0,
                specularIntensity: 0.15,
                envMapIntensity: 0.05,
            });
        }
        if (!this.M_FloorShadow) {
            this.M_FloorShadow = new ShadowMaterial({
                color: 0x000000,
                opacity: FLOOR_SHADOW_OPACITY,
                depthWrite: false,
            });
        }
        if (!this.M_FloorGlow) {
            this.M_FloorGlow = new MeshStandardMaterial({
                color: this._floorColor,
                roughness: 0.5,
            });
        }
        if (!this.M_Logo)
            this.M_Logo = new MeshStandardMaterial({ roughness: 0.3, transparent: true });
        if (!this.M_Ceilling)
            this.M_Ceilling = new MeshBasicMaterial({ color: 0xffffff, toneMapped: true });

        this.buildAll();

        const canvas = GraphicService.renderer.domElement;
        this._accessPointDragHandler = new AccessPointDragHandler(this.room, canvas);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    buildAll() {
        this.buildFloor();
        this.buildWalls();
        this.buildCeiling();
        if (this._loader.assetsloaded) {
            this.room.buildScene();
        }
    }

    clearAll(): void {
        this.room.clearAll();
        if (this._floor) this._scene.remove(this._floor);
        if (this._floorShadow) this._scene.remove(this._floorShadow);
        if (this._Ceiling) this._scene.remove(this._Ceiling);
        if (this._CMRLogo) {
            this._scene.remove(this._CMRLogo);
            this._CMRLogo = undefined;
        }
        for (const wall of this._walls) {
            this._scene.remove(wall);
        }
        this._walls = [];
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    buildFloor() {
        const geom = new PlaneGeometry(this._roomSize.width, this._roomSize.length);
        const mat = this.M_Floor;
        const floorConfig = this._loader._modelData.room.floor;
        const mapsFolder = this._loader._modelData.Paths.MapsFolder;

        const configureFloorTexture = (texture: Texture, isColourTexture = false): void => {
            texture.wrapS = RepeatWrapping;
            texture.wrapT = RepeatWrapping;
            texture.repeat.set(
                this._roomSize.width / FLOOR_TEXTURE_SIZE,
                this._roomSize.length / FLOOR_TEXTURE_SIZE
            );
            texture.anisotropy = Math.min(
                8,
                GraphicService.renderer.capabilities.getMaxAnisotropy()
            );
            texture.colorSpace = isColourTexture ? SRGBColorSpace : NoColorSpace;
            texture.needsUpdate = true;
        };

        const logoSize = this._loader._modelData.room?.logo.size;
        const geomLogo = new PlaneGeometry(logoSize.width, logoSize.length);
        const m_logo = this.M_Logo;
        if (!this.M_Logo.map) {
            const logoURL = [
                this._loader._modelData.Paths.MapsFolder,
                this._loader._modelData.room.logo.url,
            ].join("/");
            this._loader.loadTexture(logoURL, (tex) => {
                m_logo.map = tex;
                m_logo.needsUpdate = true;
            });
        }
        const logo = new Mesh(geomLogo, m_logo);
        this._Logo = logo;
        const z = Math.min(this._roomSize.length / 4.0, 2.0);
        logo.position.set(0.0, 0.01, z);
        logo.rotation.x = -Math.PI / 2;
        logo.receiveShadow = true;
        this._scene.add(logo);
        if (!this.M_FloorGlow.map) {
            if (this._loader._modelData.room.floor.glowMap) {
                const url = [
                    this._loader._modelData.Paths.MapsFolder,
                    this._loader._modelData.room.floor.glowMap,
                ].join("/");
                this.M_FloorGlow.transparent = true;
                this._loader.loadTexture(url, (tex) => {
                    this.M_FloorGlow.map = tex;
                    tex.anisotropy = 8;
                    this.M_FloorGlow.needsUpdate = true;
                });
            }
        }

        this._floorGlow = new Mesh(geom, this.M_FloorGlow);
        this._floorGlow.rotation.x = -Math.PI / 2;

        this._floorGlow.position.y = -0.002;

        this._floorGlow.castShadow = false;
        this._floorGlow.receiveShadow = false;

        this._scene.add(this._floorGlow);

        if (floorConfig.diffuseMap && !mat.map) {
            this._loader.loadTexture(`${mapsFolder}/${floorConfig.diffuseMap}`, (texture) => {
                configureFloorTexture(texture, true);
                mat.map = texture;
                mat.needsUpdate = true;
            });
        }

        if (floorConfig.normalMap && !mat.normalMap) {
            this._loader.loadTexture(`${mapsFolder}/${floorConfig.normalMap}`, (texture) => {
                configureFloorTexture(texture);
                mat.normalMap = texture;
                mat.normalScale.set(0.15, -0.15);
                mat.needsUpdate = true;
            });
        }

        if (floorConfig.roughnessMap && !mat.roughnessMap) {
            this._loader.loadTexture(`${mapsFolder}/${floorConfig.roughnessMap}`, (texture) => {
                configureFloorTexture(texture);
                mat.roughnessMap = texture;
                mat.needsUpdate = true;
            });
        }

        if (floorConfig.oaMap && !mat.aoMap) {
            this._loader.loadTexture(`${mapsFolder}/${floorConfig.oaMap}`, (texture) => {
                configureFloorTexture(texture);
                mat.aoMap = texture;
                mat.needsUpdate = true;
            });
        }

        this._floor = new Mesh(geom, mat);
        this._floor.layers.set(0);
        this._floor.name = "floor";
        this._floor.castShadow = false;
        this._floor.receiveShadow = true;
        this._floor.userData["type"] = RoomElementType.Floor;
        this._floor.rotation.x = -Math.PI / 2;
        this._scene.add(this._floor);

        this._floorShadow = new Mesh(geom, this.M_FloorShadow);
        this._floorShadow.name = "floorShadow";
        this._floorShadow.position.y = 0.002;
        this._floorShadow.rotation.x = -Math.PI / 2;
        this._floorShadow.castShadow = false;
        this._floorShadow.receiveShadow = true;
        this._scene.add(this._floorShadow);

        // Set surfaceSampler
        this._floorSampler = new MeshSurfaceSampler(this._floor).build();
    }

    buildWalls(): void {
        const geom1 = new PlaneGeometry(this._roomSize.length, this._roomSize.height);
        const geom2 = new PlaneGeometry(this._roomSize.width, this._roomSize.height);

        const cmrSize = this._loader._modelData.models.cmrLogo.size;
        const cmrModel = this._loader._modelData.models.cmrLogo;

        const cmrModelUrl = [
            this._loader._modelData.Paths.ModelPath,
            cmrModel.mesh.filename,
            cmrModel.mesh.filename + cmrModel.mesh.extention,
        ].join("/");

        this._loader.loadModel(cmrModelUrl, (gltf: any) => {
            const logo = gltf.scene as Group;

            const logoMeshes: Mesh[] = [];

            logo.traverse((child: any) => {
                if (child.isMesh) {
                    logoMeshes.push(child as Mesh);
                }
            });

            const blackMesh =
                logoMeshes.find((mesh) => mesh.name.toLowerCase() === "black") ?? logoMeshes[0];

            let emissiveMesh =
                logoMeshes.find((mesh) => mesh.name.toLowerCase().includes("emissive")) ??
                logoMeshes.find((mesh) => mesh !== blackMesh);

            if (!blackMesh) {
                console.error("CMR logo GLB contains no meshes");
                return;
            }

            if (!emissiveMesh) {
                emissiveMesh = blackMesh.clone();
                emissiveMesh.name = "CMR_Emissive_Fallback";
                emissiveMesh.position.z -= 0.001;

                logo.add(emissiveMesh);
            }

            blackMesh.material = new MeshStandardMaterial({
                name: "CMR_Black",
                color: 0x050505,
                roughness: 0.4,
                metalness: 0,
            });

            blackMesh.castShadow = true;
            blackMesh.receiveShadow = true;

            emissiveMesh.material = new MeshStandardMaterial({
                name: "CMR_Backlight",
                color: 0x202020,
                emissive: 0xf4f7ff,
                emissiveIntensity: 3,
                roughness: 1,
                metalness: 0,
                side: DoubleSide,
                toneMapped: false,
            });

            emissiveMesh.castShadow = false;
            emissiveMesh.receiveShadow = false;

            emissiveMesh.scale.multiplyScalar(1.012);

            emissiveMesh.position.z -= 0.0003;
            /*
             * Centre the imported model around the CMR logo group.
             */
            logo.updateMatrixWorld(true);

            const bounds = new Box3().setFromObject(logo);
            const size = bounds.getSize(new Vector3());
            const center = bounds.getCenter(new Vector3());

            logo.position.sub(center);

            const targetWidth = (cmrSize?.width ?? 1.6) + 1.5;
            const logoScale = targetWidth / size.x;

            const modelRoot = new Group();
            modelRoot.name = "CMRLogoModel";
            modelRoot.scale.setScalar(logoScale);
            modelRoot.add(logo);

            this._CMRLogo?.add(modelRoot);
        });

        this._CMRLogo = new Group();
        this._CMRLogo.name = "CMRLogo";
        this._scene.add(this._CMRLogo);

        this.updateCMRLogoVisibility();

        if (this._loader._modelData.room.wall.map && !this.M_Wall1.map) {
            const diffuseWallURL = [
                this._loader._modelData.Paths.MapsFolder,
                this._loader._modelData.room.wall.map,
            ].join("/");

            this._loader.loadTexture(diffuseWallURL, (texture) => {
                texture.colorSpace = SRGBColorSpace;
                texture.wrapS = texture.wrapT = RepeatWrapping;

                const texEW = texture.clone();
                texEW.anisotropy = 8;
                texEW.repeat.set(this._roomSize.length / 4, this._roomSize.height / 4);
                texEW.needsUpdate = true;

                const texNS = texture.clone();
                texNS.anisotropy = 8;
                texNS.repeat.set(this._roomSize.width / 4, this._roomSize.height / 4);
                texNS.needsUpdate = true;

                this.M_Wall1.map = texEW;
                this.M_Wall1.needsUpdate = true;

                this.M_WallNorth.map = texNS;
                this.M_WallNorth.needsUpdate = true;
            });
        }
        const wall1 = new Mesh(geom1, this.M_Wall1);
        this._walls.push(wall1);
        const wall2 = new Mesh(geom2, this.M_WallNorth);
        this._walls.push(wall2);
        const wall3 = new Mesh(geom1, this.M_Wall1);
        this._walls.push(wall3);
        const wall4 = new Mesh(geom2, this.M_WallNorth);
        this._walls.push(wall4);

        wall1.rotation.y = Math.PI / 2.0;
        wall1.position.set(-this._roomSize.width / 2.0, this._roomSize.height / 2.0, 0.0);
        wall1.receiveShadow = true;
        wall1.name = "wall1";

        wall2.position.set(0.0, this._roomSize.height / 2.0, -this._roomSize.length / 2.0);
        this._CMRLogo.position.set(
            0.0,
            this._roomSize.height / 2.0,
            -this._roomSize.length / 2.0 + 0.08
        );
        wall2.receiveShadow = true;
        wall2.name = "wall2";

        wall3.rotation.y = -Math.PI / 2.0;
        wall3.position.set(this._roomSize.width / 2.0, this._roomSize.height / 2.0, 0.0);
        wall3.receiveShadow = true;
        wall3.name = "wall3";

        wall4.rotation.y = Math.PI;
        wall4.position.set(0.0, this._roomSize.height / 2.0, this._roomSize.length / 2.0);
        wall4.receiveShadow = true;
        wall4.name = "wall4";

        this._scene.add(wall1);
        this._scene.add(wall2);
        this._scene.add(wall3);
        this._scene.add(wall4);
    }

    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    buildCeiling(): void {
        const geom = new PlaneGeometry(this._roomSize.width, this._roomSize.length);

        const ceiling = new Mesh(geom, this.M_Ceilling);

        this._Ceiling = ceiling;

        ceiling.name = "ceiling";
        ceiling.position.y = this._roomSize.height;
        ceiling.rotation.x = Math.PI / 2;

        ceiling.castShadow = false;
        ceiling.receiveShadow = false;

        this._scene.add(ceiling);
    }
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    updateRoomSize(newSize: IRoomSize) {
        this._roomSize = { ...newSize };
        this.room.currentConfiguration = {
            ...this.room.currentConfiguration,
            roomSize: { ...newSize },
        };
        // update Walls
        const geom1 = new PlaneGeometry(this._roomSize.length, this._roomSize.height);
        const geom2 = new PlaneGeometry(this._roomSize.width, this._roomSize.height);
        if (this.M_Wall1.normalMap) {
            this.M_Wall1.normalMap.repeat.set(this._roomSize.width * 4, this._roomSize.height * 4);
            this.M_Wall1.needsUpdate = true;
        }
        if (this.M_Wall2.normalMap) {
            this.M_Wall2.normalMap.repeat.set(this._roomSize.length * 4, this._roomSize.height * 4);
            this.M_Wall2.needsUpdate = true;
        }
        this._walls[0].geometry = geom1;
        this._walls[0].position.set(-this._roomSize.width / 2.0, this._roomSize.height / 2.0, 0.0);

        this._walls[2].geometry = geom1;
        this._walls[2].position.set(this._roomSize.width / 2.0, this._roomSize.height / 2.0, 0.0);

        this._walls[1].geometry = geom2;
        this._walls[1].position.set(0.0, this._roomSize.height / 2.0, -this._roomSize.length / 2.0);

        this._walls[3].geometry = geom2;
        this._walls[3].position.set(0.0, this._roomSize.height / 2.0, this._roomSize.length / 2.0);
        //update Floor
        if (this._floor) {
            const geom = new PlaneGeometry(this._roomSize.width, this._roomSize.length);
            this._floor.geometry = geom;
            const floorRepeatX = this._roomSize.width / FLOOR_TEXTURE_SIZE;
            const floorRepeatY = this._roomSize.length / FLOOR_TEXTURE_SIZE;
            const floorTextures = [
                this.M_Floor.map,
                this.M_Floor.normalMap,
                this.M_Floor.roughnessMap,
                this.M_Floor.aoMap,
            ];

            floorTextures.forEach((texture) => {
                if (!texture) return;

                texture.repeat.set(floorRepeatX, floorRepeatY);
                texture.needsUpdate = true;
            });
            this.M_Floor.needsUpdate = true;
            if (this._floorGlow) {
                this._floorGlow.geometry = geom;
            }
            if (this._floorShadow) {
                this._floorShadow.geometry = geom;
            }
        }
        //update Logo
        if (this._Logo) {
            const z = Math.min(this._roomSize.length / 4.0, 2.0);
            this._Logo.position.set(0.0, 0.01, z);
        }
        if (this._CMRLogo) {
            this._CMRLogo.position.set(
                0.0,
                this._roomSize.height / 2,
                -this._roomSize.length / 2 + 0.012
            );
        }
        //update Ceiling
        if (this._Ceiling) {
            const CeilingGeom = new PlaneGeometry(this._roomSize.width, this._roomSize.length);
            this._Ceiling.geometry = CeilingGeom;
            this._Ceiling.position.y = this._roomSize.height;
            if (this.M_Ceilling.normalMap) {
                this.M_Ceilling.normalMap.repeat.x = this._roomSize.width * 4;
                this.M_Ceilling.normalMap.repeat.y = this._roomSize.length * 4;
            }
        }

        this.room.refreshAccessPoints();
    }
    // eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types
    reset(sceneConf: ISceneConfiguration) {
        const resetConfiguration = JSON.parse(JSON.stringify(sceneConf)) as ISceneConfiguration;

        this.updateRoomSize(resetConfiguration.roomSize);
        this.room.reset(resetConfiguration);
        this.room.setPatientLocked(false);
    }

    addAccessPoint(wall: "north" | "south" | "east" | "west" = "north", offset = 0): void {
        const scene = JSON.parse(JSON.stringify(this.room.currentConfiguration));

        const id = crypto.getRandomValues(new Uint32Array(2)).join("-");

        if (!scene.accessPoints) scene.accessPoints = [];

        scene.accessPoints.push({
            id,
            wall,
            offset,
        });

        this.room.currentConfiguration = scene;
        this.room.buildAccessPoints();
    }

    private updateCMRLogoVisibility(): void {
        if (!this._CMRLogo) {
            return;
        }

        const hasNorthAccessPoint =
            this.room.currentConfiguration.accessPoints?.some(
                (accessPoint) => accessPoint.wall === "north"
            ) ?? false;

        this._CMRLogo.visible = !hasNorthAccessPoint;
    }
}
